import { useMemo, useState } from 'react';
import { Lock, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ExportFieldGroup } from '@/lib/export';

interface FieldCatalogProps {
  groups: ExportFieldGroup[];
  selectedIds: Set<string>;
  onToggle: (fieldId: string) => void;
}

/** Searchable, grouped list of available fields. Checking a field adds/removes a column. */
export function FieldCatalog({ groups, selectedIds, onToggle }: FieldCatalogProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, fields: g.fields.filter((f) => f.label.toLowerCase().includes(q)) }))
      .filter((g) => g.fields.length > 0);
  }, [groups, query]);

  return (
    <div className="flex h-full flex-col">
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fields…"
          className="pl-9"
        />
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto pr-1">
        {filtered.length === 0 && (
          <p className="px-1 py-6 text-center text-sm text-muted-foreground">No fields match “{query}”.</p>
        )}
        {filtered.map((group) => {
          // FOP payment details (IBAN, tax ID) are as sensitive as pay. [HW-1008]
          const sensitive = group.key === 'compensation' || group.key === 'payment';
          return (
            <div key={group.key}>
              <div className="mb-1.5 flex items-center gap-1.5 px-1">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </h4>
                {sensitive && <Lock className="h-3 w-3 text-amber-500" aria-label="Sensitive fields" />}
              </div>
              <div className="space-y-0.5">
                {group.fields.map((field) => {
                  const checked = selectedIds.has(field.id);
                  return (
                    <label
                      key={field.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted/70',
                        checked && 'bg-muted/50',
                      )}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => onToggle(field.id)} />
                      <span className="flex-1 truncate">{field.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
